import React, { useState, useEffect, useRef, useMemo } from "react";
import { Switch, Transition } from "@headlessui/react";
import {
  CheckCircleIcon,
  XCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/solid";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { LuCopy, LuCopyCheck } from "react-icons/lu";
import { Lock, KeyRound, Pencil, Plus, X } from "lucide-react";
import { MdSupportAgent } from "react-icons/md";
import {
  OpenAI as OpenAIIcon,
  Anthropic as AnthropicIcon,
  Gemini as GeminiIcon,
  XAI as XAIIcon,
  Mistral as MistralIcon,
  DeepSeek as DeepSeekIcon,
  Cohere as CohereIcon,
  Azure as AzureIcon,
  AzureAI as AzureAIIcon,
  Bedrock as BedrockIcon,
  Aws as AwsIcon,
  VertexAI as VertexAIIcon,
  Groq as GroqIcon,
  Fireworks as FireworksIcon,
  Together as TogetherAIIcon,
  DeepInfra as DeepInfraIcon,
  Replicate as ReplicateIcon,
  Perplexity as PerplexityIcon,
  Cerebras as CerebrasIcon,
  Ai21 as Ai21Icon,
  Cloudflare as CloudflareIcon,
  Ollama as OllamaIcon,
  HuggingFace as HuggingFaceIcon,
  OpenRouter as OpenRouterIcon,
  Vllm as VllmIcon,
  Anyscale as AnyscaleIcon,
  LmStudio as LMStudioIcon,
  NPLCloud as NPLCloudIcon,
} from "@lobehub/icons";
import {
  getUserType,
  getAllUsers,
  adminNewUser,
  adminLockAccount,
  adminResetPassword,
  adminUpdateAccount,
  getMfaToggle,
  updateMfa,
  getUserFirstLast,
  userUpdateAccount,
  userUpdatePassword,
  getAllUsersTickets,
  getTickets,
  getTicketThread,
  addNewTicketData,
  getPublicModels,
  updatePublicModel,
  getLitellmModels,
} from "../../api/settings";

import { Input } from "../Universal/Input";
import { Button } from "../Universal/Button";
import { StripeOverlay } from "../Universal/StripeOverlay";
import { usePopup } from "../contexts/PopupContext";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

/* ─────────────────────────────────────────────────────────────
   PasswordField
───────────────────────────────────────────────────────────── */
const PasswordField = ({
  label,
  value,
  onChange,
  requirements,
  showCopy = true,
  copied = false,
  onCopy = () => {},
}) => (
  <div className="rounded-lg border border-surfaceBorder bg-innerBg p-3">
    <label className="mb-1.5 block text-[0.8125rem] font-normal tracking-wide text-mutedText">
      {label}
    </label>
    <div className="mt-2 flex gap-2">
      <input
        type="text"
        value={value}
        onChange={onChange}
        className="w-full rounded-lg border border-inputBorder bg-transparent px-3 py-2 text-sm text-mainText outline-none ring-inputFocus/60 transition focus:ring-2 placeholder:text-mutedText"
      />
      {showCopy &&
        (copied ? (
          <button className="shrink-0 rounded-lg border border-surfaceBorder bg-transparent p-2">
            <LuCopyCheck className="h-5 w-5 text-activeCase" />
          </button>
        ) : (
          <CopyToClipboard text={value} onCopy={onCopy}>
            <button className="shrink-0 rounded-lg border border-btnBorder bg-transparent p-2 transition hover:bg-btnHover cursor-pointer outline-none ring-inputFocus/60 focus-visible:ring-2">
              <LuCopy className="h-5 w-5 text-mutedText" />
            </button>
          </CopyToClipboard>
        ))}
    </div>
    <div className="mt-2 space-y-1 text-xs">
      {!requirements.length && (
        <div className="text-errorText">Password must be 10 characters</div>
      )}
      {!requirements.upper && (
        <div className="text-errorText">Need uppercase letter</div>
      )}
      {!requirements.lower && (
        <div className="text-errorText">Need lowercase letter</div>
      )}
      {!requirements.number && (
        <div className="text-errorText">Need a number</div>
      )}
      {!requirements.symbol && (
        <div className="text-errorText">Need a symbol</div>
      )}
    </div>
  </div>
);

/* ─────────────────────────────────────────────────────────────
   UserInfoDisplay
───────────────────────────────────────────────────────────── */
const UserInfoDisplay = ({ user }) => (
  <div className="mt-3 text-sm text-mutedText space-y-1">
    <div>
      Name:{" "}
      <span className="text-mainText">
        {user.first} {user.last}
      </span>
    </div>
    <div>
      Username: <span className="text-mainText">{user.email}</span>
    </div>
    <div>
      Role: <span className="text-mainText">{user.role}</span>
    </div>
  </div>
);

/* ─────────────────────────────────────────────────────────────
   Toast
───────────────────────────────────────────────────────────── */
const Toast = ({ show, type, onClose }) => (
  <div
    className={classNames(
      show
        ? "opacity-100 translate-y-0"
        : "opacity-0 translate-y-2 pointer-events-none",
      "transition transform duration-300",
    )}
  >
    <div
      className={`pointer-events-auto w-[280px] rounded-lg border p-3 backdrop-blur-md ${
        type === "success"
          ? "border-activeCase/30 bg-activeCase/10"
          : "border-errorText/30 bg-errorText/10"
      }`}
    >
      <div className="flex items-center gap-2">
        {type === "success" ? (
          <CheckCircleIcon className="h-6 w-6 text-activeCase" />
        ) : (
          <XCircleIcon className="h-6 w-6 text-errorText" />
        )}
        <p className="text-sm text-mainText">
          {type === "success" ? "Success!" : "Error"}
        </p>
        <button
          className="ml-auto p-1 rounded-lg transition hover:bg-btnHover cursor-pointer outline-none ring-inputFocus/60 focus-visible:ring-2"
          onClick={onClose}
          aria-label="Close"
        >
          <XMarkIcon className="h-5 w-5 text-mutedText" />
        </button>
      </div>
    </div>
  </div>
);

/* ─────────────────────────────────────────────────────────────
   usePasswordValidation
───────────────────────────────────────────────────────────── */
const usePasswordValidation = (password) => {
  const [requirements, setRequirements] = useState({
    length: false,
    upper: false,
    lower: false,
    number: false,
    symbol: false,
  });

  useEffect(() => {
    setRequirements({
      length: password.length >= 10,
      upper: /[A-Z]/.test(password),
      lower: /[a-z]/.test(password),
      number: /\d/.test(password),
      symbol: /[|\\/~^:,;?!&%$@*+]/.test(password),
    });
  }, [password]);

  const allMet = Object.values(requirements).every(Boolean);
  return { requirements, allMet };
};

/* ─────────────────────────────────────────────────────────────
   AdminUserManagement
───────────────────────────────────────────────────────────── */
const AdminUserManagement = () => {
  const [userList, setUserList] = useState([]);
  const [view, setView] = useState("list");
  const [expanded, setExpanded] = useState(null);

  const [newUserForm, setNewUserForm] = useState({
    email: "",
    mfaEmail: "",
    firstName: "",
    lastName: "",
    role: "user",
    password: "",
  });
  const [newUserPasswordCopied, setNewUserPasswordCopied] = useState(false);
  const newUserPasswordValidation = usePasswordValidation(newUserForm.password);

  const [editUserForm, setEditUserForm] = useState({
    originalEmail: "",
    email: "",
    mfaEmail: "",
    firstName: "",
    lastName: "",
    role: "",
  });

  const [resetPasswordForm, setResetPasswordForm] = useState({
    userInfo: {},
    password: "",
  });
  const [resetPasswordCopied, setResetPasswordCopied] = useState(false);
  const resetPasswordValidation = usePasswordValidation(
    resetPasswordForm.password,
  );

  const [lockUserForm, setLockUserForm] = useState({
    userInfo: {},
    toggleEnabled: false,
  });
  const [toast, setToast] = useState({ show: false, type: "success" });

  // VN select pattern
  const selectCls =
    "mt-2 block w-full appearance-none rounded-lg bg-transparent text-mainText border border-inputBorder px-3 py-2.5 text-sm outline-none ring-inputFocus/60 transition focus:ring-2";

  // VN icon button pattern
  const iconBtn =
    "relative inline-flex items-center justify-center rounded-lg p-2 border border-btnBorder bg-transparent text-mutedText transition hover:bg-btnHover hover:text-mainText outline-none ring-inputFocus/60 focus-visible:ring-2 cursor-pointer";

  useEffect(() => {
    const loadUsers = async () => {
      const response = await getAllUsers();
      const users =
        response?.data?.map((u) => ({
          email: u.email,
          first: u.first_name,
          last: u.last_name,
          role: u.role,
          mfa_email: u.mfa_email,
        })) || [];
      setUserList(users);
    };
    loadUsers();
  }, [toast]);

  useEffect(() => {
    setResetPasswordCopied(false);
  }, [resetPasswordForm.password]);
  useEffect(() => {
    setNewUserPasswordCopied(false);
  }, [newUserForm.password]);

  const showToast = (type) => {
    setToast({ show: true, type });
    setTimeout(() => setToast({ show: false, type }), 4000);
  };

  const openNewUser = () => setView("new");
  const openEditInline = (user) => {
    setEditUserForm({
      originalEmail: user.email,
      email: user.email,
      mfaEmail: user.mfa_email,
      firstName: user.first,
      lastName: user.last,
      role: user.role,
    });
    setExpanded({ email: user.email, type: "edit" });
  };
  const openResetInline = (user) => {
    setResetPasswordForm({ userInfo: user, password: "" });
    setExpanded({ email: user.email, type: "reset" });
  };
  const openLockInline = (user) => {
    setLockUserForm({ userInfo: user, toggleEnabled: false });
    setExpanded({ email: user.email, type: "lock" });
  };
  const collapseInline = () => setExpanded(null);
  const backToList = () => {
    setView("list");
    setNewUserForm({
      email: "",
      mfaEmail: "",
      firstName: "",
      lastName: "",
      role: "user",
      password: "",
    });
    setNewUserPasswordCopied(false);
  };

  const addNewUser = async () => {
    const response = await adminNewUser(
      newUserForm.email,
      newUserForm.mfaEmail,
      newUserForm.firstName,
      newUserForm.lastName,
      newUserForm.role,
      newUserForm.password,
    );
    if (response?.data?.message === "success") {
      showToast("success");
      backToList();
    } else showToast("error");
  };

  const editUser = async () => {
    const response = await adminUpdateAccount(
      editUserForm.originalEmail,
      ["first_name", "last_name", "email", "mfa_email", "user_role"],
      [
        editUserForm.firstName,
        editUserForm.lastName,
        editUserForm.email,
        editUserForm.mfaEmail,
        editUserForm.role,
      ],
    );
    if (response?.data?.message === "success") {
      showToast("success");
      collapseInline();
    } else showToast("error");
  };

  const resetPassword = async () => {
    const response = await adminResetPassword(
      resetPasswordForm.userInfo.email,
      resetPasswordForm.password,
    );
    if (response?.data?.message === "success") {
      showToast("success");
      collapseInline();
    } else showToast("error");
  };

  const lockUser = async () => {
    const response = await adminLockAccount(lockUserForm.userInfo.email);
    if (response?.data?.message === "success") {
      showToast("success");
      collapseInline();
    } else showToast("error");
  };

  return (
    <div className="h-full w-full flex flex-col">
      <div className="flex items-center justify-between px-6 py-4 border-b border-mainDivider shrink-0">
        <h2 className="text-sm font-medium text-mainText">
          {view === "list" ? "Users" : "Add User"}
        </h2>
        {view === "list" && (
          <button
            className={iconBtn}
            aria-label="Add user"
            onClick={openNewUser}
            title="Add user"
          >
            <Plus className="h-4 w-4 text-velBlue" />
          </button>
        )}
      </div>

      <div className="flex-1 overflow-auto overscroll-contain px-6 py-4">
        {/* ── User list ── */}
        {view === "list" && (
          <div className="space-y-2">
            {userList.map((user) => {
              const isThisExpanded = expanded && expanded.email === user.email;
              const type = isThisExpanded ? expanded.type : null;

              return (
                <div
                  key={user.email}
                  className="relative group rounded-lg border border-inputBorder bg-innerBg transition-all duration-200 hover:bg-tableRowHover hover:border-surfaceBorder"
                >
                  <div className="relative z-10 p-3">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <h3
                            className="text-sm font-medium text-mainText truncate"
                            title={user.email}
                          >
                            {user.email}
                          </h3>
                          <span className="inline-flex items-center rounded-full border border-surfaceBorder bg-surface/60 px-2 py-0.5 text-xs text-mutedText shrink-0">
                            {user.role}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-mutedText">
                          <span className="truncate">{user.first}</span>
                          <span className="truncate">{user.last}</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          className={iconBtn}
                          aria-label="Lock account"
                          title="Lock account"
                          onClick={() =>
                            isThisExpanded && type === "lock"
                              ? collapseInline()
                              : openLockInline(user)
                          }
                        >
                          <Lock className="h-4 w-4 text-velBlue" />
                        </button>
                        <button
                          className={iconBtn}
                          aria-label="Reset password"
                          title="Reset password"
                          onClick={() =>
                            isThisExpanded && type === "reset"
                              ? collapseInline()
                              : openResetInline(user)
                          }
                        >
                          <KeyRound className="h-4 w-4 text-velBlue" />
                        </button>
                        <button
                          className={iconBtn}
                          aria-label="Edit user"
                          title="Edit user"
                          onClick={() =>
                            isThisExpanded && type === "edit"
                              ? collapseInline()
                              : openEditInline(user)
                          }
                        >
                          <Pencil className="h-4 w-4 text-mutedText" />
                        </button>
                      </div>
                    </div>

                    {/* Inline expander */}
                    {isThisExpanded && (
                      <div className="mt-3 rounded-lg border border-inputBorder bg-innerBg p-3">
                        {type === "edit" && (
                          <div className="grid grid-cols-1 gap-3">
                            <div className="grid grid-cols-2 gap-3">
                              <Input
                                label="First Name"
                                value={editUserForm.firstName}
                                onChange={(e) =>
                                  setEditUserForm((p) => ({
                                    ...p,
                                    firstName: e.target.value,
                                  }))
                                }
                              />
                              <Input
                                label="Last Name"
                                value={editUserForm.lastName}
                                onChange={(e) =>
                                  setEditUserForm((p) => ({
                                    ...p,
                                    lastName: e.target.value,
                                  }))
                                }
                              />
                            </div>
                            <Input
                              label="Username"
                              value={editUserForm.email}
                              onChange={(e) =>
                                setEditUserForm((p) => ({
                                  ...p,
                                  email: e.target.value,
                                }))
                              }
                            />
                            <div>
                              <label className="mb-1.5 block text-[0.8125rem] font-normal tracking-wide text-mutedText">
                                User Role
                              </label>
                              <select
                                className={selectCls}
                                value={editUserForm.role}
                                onChange={(e) =>
                                  setEditUserForm((p) => ({
                                    ...p,
                                    role: e.target.value,
                                  }))
                                }
                              >
                                <option>user</option>
                                <option>admin</option>
                              </select>
                            </div>
                            <Input
                              label="MFA Email"
                              value={editUserForm.mfaEmail}
                              onChange={(e) =>
                                setEditUserForm((p) => ({
                                  ...p,
                                  mfaEmail: e.target.value,
                                }))
                              }
                            />
                            <div className="mt-2 flex gap-2">
                              <Button variant="ghost" onClick={collapseInline}>
                                Cancel
                              </Button>
                              <Button onClick={editUser}>Submit</Button>
                            </div>
                          </div>
                        )}

                        {type === "reset" && (
                          <div>
                            <UserInfoDisplay
                              user={resetPasswordForm.userInfo}
                            />
                            <div className="mt-3">
                              <PasswordField
                                label="New Password"
                                value={resetPasswordForm.password}
                                onChange={(e) =>
                                  setResetPasswordForm((p) => ({
                                    ...p,
                                    password: e.target.value,
                                  }))
                                }
                                requirements={
                                  resetPasswordValidation.requirements
                                }
                                copied={resetPasswordCopied}
                                onCopy={() => setResetPasswordCopied(true)}
                              />
                              <p className="mt-2 text-xs text-mutedText">
                                Provide the new password to the user if they do
                                not already know it.
                              </p>
                            </div>
                            <div className="mt-3 flex gap-2">
                              <Button variant="ghost" onClick={collapseInline}>
                                Cancel
                              </Button>
                              {resetPasswordValidation.allMet ? (
                                <Button onClick={resetPassword}>Submit</Button>
                              ) : (
                                <Button disabled>Locked</Button>
                              )}
                            </div>
                          </div>
                        )}

                        {type === "lock" && (
                          <div>
                            <UserInfoDisplay user={lockUserForm.userInfo} />
                            <div className="mt-3 flex items-center justify-between rounded-lg border border-inputBorder bg-innerBg p-3">
                              <p className="text-sm text-mainText">
                                Switch toggle to enable control
                              </p>
                              {/* VN toggle pattern */}
                              <button
                                role="switch"
                                aria-checked={lockUserForm.toggleEnabled}
                                onClick={() =>
                                  setLockUserForm((p) => ({
                                    ...p,
                                    toggleEnabled: !p.toggleEnabled,
                                  }))
                                }
                                className={classNames(
                                  lockUserForm.toggleEnabled
                                    ? "bg-velBlue border-velBlue"
                                    : "bg-transparent border-inputBorder",
                                  "relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border transition-colors outline-none ring-inputFocus/60 focus:ring-2",
                                )}
                              >
                                <span
                                  aria-hidden="true"
                                  className={classNames(
                                    lockUserForm.toggleEnabled
                                      ? "translate-x-5"
                                      : "translate-x-0.5",
                                    "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-mainText shadow-sm transition",
                                  )}
                                />
                              </button>
                            </div>
                            <div className="mt-3 flex gap-2">
                              <Button variant="ghost" onClick={collapseInline}>
                                Cancel
                              </Button>
                              {lockUserForm.toggleEnabled ? (
                                <Button
                                  variant="destructive"
                                  onClick={lockUser}
                                >
                                  Lock
                                </Button>
                              ) : (
                                <Button disabled>Disabled</Button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── New user form ── */}
        {view === "new" && (
          <div className="mt-2">
            <div className="mt-4 grid grid-cols-1 gap-3">
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="First Name"
                  value={newUserForm.firstName}
                  onChange={(e) =>
                    setNewUserForm((p) => ({ ...p, firstName: e.target.value }))
                  }
                />
                <Input
                  label="Last Name"
                  value={newUserForm.lastName}
                  onChange={(e) =>
                    setNewUserForm((p) => ({ ...p, lastName: e.target.value }))
                  }
                />
              </div>
              <Input
                label="Username"
                value={newUserForm.email}
                onChange={(e) =>
                  setNewUserForm((p) => ({ ...p, email: e.target.value }))
                }
              />
              <div>
                <label className="mb-1.5 block text-[0.8125rem] font-normal tracking-wide text-mutedText">
                  User Role
                </label>
                <select
                  className={selectCls}
                  value={newUserForm.role}
                  onChange={(e) =>
                    setNewUserForm((p) => ({ ...p, role: e.target.value }))
                  }
                >
                  <option>user</option>
                  <option>admin</option>
                </select>
              </div>
              <PasswordField
                label="User Password"
                value={newUserForm.password}
                onChange={(e) =>
                  setNewUserForm((p) => ({ ...p, password: e.target.value }))
                }
                requirements={newUserPasswordValidation.requirements}
                copied={newUserPasswordCopied}
                onCopy={() => setNewUserPasswordCopied(true)}
              />
              <div className="text-xs text-mutedText mt-1">
                When creating a new user the admin must create the first
                password and provide it to the user.
              </div>
              <div className="rounded-lg border border-surfaceBorder bg-innerBg p-3">
                <Input
                  label="MFA Email"
                  value={newUserForm.mfaEmail}
                  onChange={(e) =>
                    setNewUserForm((p) => ({ ...p, mfaEmail: e.target.value }))
                  }
                />
                <p className="mt-1 text-xs text-mutedText">
                  Best practice to provide an MFA email even if MFA is not yet
                  enabled.
                </p>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <Button variant="ghost" onClick={backToList}>
                Cancel
              </Button>
              {newUserPasswordValidation.allMet ? (
                <Button onClick={addNewUser}>Submit</Button>
              ) : (
                <Button disabled>Locked</Button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Toast */}
      <div className="pointer-events-none fixed bottom-4 right-4 flex flex-col gap-2">
        <Toast
          show={toast.show && toast.type === "success"}
          type="success"
          onClose={() => setToast({ show: false, type: "success" })}
        />
        <Toast
          show={toast.show && toast.type === "error"}
          type="error"
          onClose={() => setToast({ show: false, type: "error" })}
        />
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   Banner
───────────────────────────────────────────────────────────── */
const Banner = ({ show, isError, message }) => {
  if (!show) return null;
  return (
    <div
      className={`mb-4 px-4 py-3 rounded-lg border backdrop-blur-md ${
        isError
          ? "border-errorText/30 bg-errorText/10"
          : "border-activeCase/30 bg-activeCase/10"
      }`}
      role="alert"
      aria-live="polite"
    >
      <div
        className={`text-sm ${isError ? "text-errorText" : "text-activeCase"}`}
      >
        {message}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   AdminSecurity
───────────────────────────────────────────────────────────── */
function AdminSecurity() {
  const [mfaEnabled, setMfaEnabled] = useState(null);
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const fetchMfaToggle = async () => {
      const response = await getMfaToggle();
      setMfaEnabled(!!response?.data?.mfa?.mfa);
    };
    fetchMfaToggle();
  }, []);

  const showBannerWithTimeout = () => {
    setShowBanner(true);
    setTimeout(() => setShowBanner(false), 3000);
  };

  const updateMFAcall = async (newValue) => {
    const oldValue = mfaEnabled;
    setMfaEnabled(newValue);
    const response = await updateMfa(newValue);
    if (response?.status === 200 && response?.data?.mfa === "success") {
      setMessage("MFA updated successfully!");
      setIsError(false);
    } else {
      setMessage("Failed to update MFA. Please try again.");
      setIsError(true);
      setMfaEnabled(oldValue);
    }
    showBannerWithTimeout();
  };

  return (
    <div className="px-6 py-6 max-w-3xl">
      <Banner show={showBanner} isError={isError} message={message} />
      <div className="rounded-lg border border-surfaceBorder bg-innerBg p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-medium text-mainText">
              Multi-Factor Authentication
            </h2>
            <p className="mt-1 text-sm text-mutedText">
              Require MFA for account sign-in.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span
              className={`text-[10px] uppercase tracking-wide px-2 py-0.5 ${
                mfaEnabled ? "text-activeCase" : "text-mutedText"
              }`}
            >
              {mfaEnabled ? "Enabled" : "Disabled"}
            </span>
            {/* VN toggle via HeadlessUI Switch */}
            <Switch
              checked={!!mfaEnabled}
              onChange={updateMFAcall}
              className={classNames(
                mfaEnabled
                  ? "bg-velBlue border-velBlue"
                  : "bg-transparent border-inputBorder",
                "relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border transition-colors outline-none ring-inputFocus/60 focus:ring-2",
              )}
            >
              <span
                aria-hidden="true"
                className={classNames(
                  mfaEnabled ? "translate-x-5" : "translate-x-0.5",
                  "inline-block h-5 w-5 transform rounded-full bg-mainText shadow-sm transition",
                )}
              />
            </Switch>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   UserToast
───────────────────────────────────────────────────────────── */
const UserToast = ({ open, type = "success", onClose, children }) => {
  const Icon = type === "success" ? CheckCircleIcon : XCircleIcon;
  return (
    <Transition
      show={open}
      enter="transform ease-out duration-200"
      enterFrom="translate-y-2 opacity-0"
      enterTo="translate-y-0 opacity-100"
      leave="transition ease-in duration-150"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <div
        className="fixed bottom-6 right-6 z-[100] w-[min(92vw,340px)]"
        role="status"
        aria-live="polite"
      >
        <div
          className={`rounded-lg border backdrop-blur-md p-4 ${
            type === "success"
              ? "border-activeCase/30 bg-activeCase/10"
              : "border-errorText/30 bg-errorText/10"
          }`}
        >
          <div className="flex items-start gap-3">
            <Icon
              className={`h-6 w-6 ${type === "success" ? "text-activeCase" : "text-errorText"}`}
              aria-hidden="true"
            />
            <div className="flex-1 text-sm text-mainText">{children}</div>
            <button
              onClick={onClose}
              className="p-1 rounded-lg transition hover:bg-btnHover outline-none ring-inputFocus/60 focus:ring-2 cursor-pointer"
              aria-label="Close"
            >
              <XMarkIcon className="h-5 w-5 text-mutedText" />
            </button>
          </div>
        </div>
      </div>
    </Transition>
  );
};

/* ─────────────────────────────────────────────────────────────
   UserGeneral
───────────────────────────────────────────────────────────── */
function UserGeneral() {
  const [userFirst, setUserFirst] = useState("");
  const [userLast, setUserLast] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [userMfa, setUserMfa] = useState("");
  const [infoRefresh, setInfoRefresh] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    getUserFirstLast()
      .then((response) => {
        setUserFirst(response.data.first_name);
        setUserLast(response.data.last_name);
        setUserEmail(response.data.email);
        setUserMfa(response.data.mfa_email || "");
      })
      .catch((error) => {
        if (error.message === "Request failed with status code 401") {
          localStorage.setItem("user", "");
          window.location.href = "/login";
        }
      });
  }, []);

  useEffect(() => {
    if (userFirst !== "" && infoRefresh) {
      userUpdateAccount(
        ["first_name", "last_name", "email", "mfa_email"],
        [userFirst, userLast, userEmail, userMfa],
      ).then((response) => {
        if (response.data.message === "success") {
          setShowSuccess(true);
          setTimeout(() => setShowSuccess(false), 3000);
        } else {
          setShowError(true);
          setTimeout(() => setShowError(false), 3000);
        }
      });
    }
  }, [infoRefresh]);

  return (
    <div className="px-6 py-6 max-w-3xl">
      <div className="rounded-lg border border-surfaceBorder bg-innerBg p-6">
        <div className="mb-6">
          <h2 className="text-base font-medium text-mainText">Account</h2>
          <p className="text-sm text-mutedText mt-1">
            Update your profile information.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6">
          <div className="sm:col-span-3">
            <Input
              label="First Name"
              value={userFirst}
              onChange={(e) => setUserFirst(e.target.value)}
            />
          </div>
          <div className="sm:col-span-3">
            <Input
              label="Last Name"
              value={userLast}
              onChange={(e) => setUserLast(e.target.value)}
            />
          </div>
          <div className="sm:col-span-3">
            <Input
              label="Username"
              value={userEmail}
              onChange={(e) => setUserEmail(e.target.value)}
            />
          </div>
          <div className="sm:col-span-3">
            <Input
              label="MFA Email Address"
              value={userMfa}
              onChange={(e) => setUserMfa(e.target.value)}
            />
          </div>
        </div>
        <div className="mt-6 flex justify-end">
          <Button onClick={() => setInfoRefresh((s) => !s)}>Save</Button>
        </div>
      </div>

      <UserToast
        open={showSuccess}
        type="success"
        onClose={() => setShowSuccess(false)}
      >
        <span className="text-mainText">Saved.</span> Your settings were updated
        successfully.
      </UserToast>
      <UserToast
        open={showError}
        type="error"
        onClose={() => setShowError(false)}
      >
        <span className="text-mainText">Update failed.</span> Please try again.
      </UserToast>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   SecurityToast (same pattern as UserToast, non-fixed positioning)
───────────────────────────────────────────────────────────── */
const SecurityToast = ({ open, type = "success", onClose, children }) => {
  const Icon = type === "success" ? CheckCircleIcon : XCircleIcon;
  return (
    <Transition
      show={open}
      enter="transform ease-out duration-200"
      enterFrom="translate-y-2 opacity-0"
      enterTo="translate-y-0 opacity-100"
      leave="transition ease-in duration-150"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <div
        className="pointer-events-auto w-full max-w-sm"
        role="status"
        aria-live="polite"
      >
        <div
          className={`rounded-lg border backdrop-blur-md p-4 ${
            type === "success"
              ? "border-activeCase/30 bg-activeCase/10"
              : "border-errorText/30 bg-errorText/10"
          }`}
        >
          <div className="flex items-start gap-3">
            <Icon
              className={`h-6 w-6 ${type === "success" ? "text-activeCase" : "text-errorText"}`}
              aria-hidden="true"
            />
            <p className="text-sm text-mainText">{children}</p>
            <button
              onClick={onClose}
              className="ml-auto p-1 rounded-lg transition hover:bg-btnHover outline-none ring-inputFocus/60 focus:ring-2 cursor-pointer"
              aria-label="Close"
            >
              <XMarkIcon
                className="h-5 w-5 text-mutedText"
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
      </div>
    </Transition>
  );
};

/* ─────────────────────────────────────────────────────────────
   UserSecurity
───────────────────────────────────────────────────────────── */
function UserSecurity() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [resetPassword, setResetPassword] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    if (newPassword !== "" && resetPassword) {
      userUpdatePassword(currentPassword, confirmNewPassword).then(
        (response) => {
          if (response.data.message === "success") {
            setShowSuccess(true);
            setTimeout(() => setShowSuccess(false), 4000);
            setCurrentPassword("");
            setNewPassword("");
            setConfirmNewPassword("");
          } else {
            setShowError(true);
            setTimeout(() => setShowError(false), 4000);
          }
        },
      );
    }
  }, [resetPassword]);

  return (
    <div className="px-6 py-6 max-w-3xl">
      <div className="rounded-lg border border-surfaceBorder bg-innerBg p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <h2 className="text-base font-medium text-mainText">
              Change Password
            </h2>
            <p className="mt-1 text-sm text-mutedText">
              Update your password associated with your account.
            </p>
          </div>
          <div className="md:col-span-2">
            <div className="grid grid-cols-1 gap-y-4 sm:max-w-xl sm:grid-cols-6">
              <div className="col-span-full">
                <Input
                  label="Current Password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  type="password"
                />
              </div>
              <div className="col-span-full">
                <Input
                  label="New Password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  type="password"
                />
              </div>
              <div className="col-span-full">
                <Input
                  label="Confirm Password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  type="password"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <Button onClick={() => setResetPassword((v) => !v)}>Save</Button>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-4 flex w-full flex-col items-center space-y-4 sm:items-end">
        <SecurityToast
          open={showSuccess}
          type="success"
          onClose={() => setShowSuccess(false)}
        >
          Password updated successfully!
        </SecurityToast>
        <SecurityToast
          open={showError}
          type="error"
          onClose={() => setShowError(false)}
        >
          Failed to update password. Please try again.
        </SecurityToast>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Tickets helpers
───────────────────────────────────────────────────────────── */
function formatTimestamp(timestampSec) {
  const date = new Date(timestampSec * 1000);
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const getLocalPart = (email) => {
  const at = (email || "").indexOf("@");
  return at === -1 ? email : email.substring(0, at);
};

/* ─────────────────────────────────────────────────────────────
   Tickets
───────────────────────────────────────────────────────────── */
const Tickets = ({ isAdmin = false }) => {
  const { setIsScreenshotPopUpOpen, setCurrentScreenshotForPopUp } = usePopup();
  const [tickets, setTickets] = useState(null);
  const [currentTicketId, setCurrentTicketId] = useState(null);
  const [thread, setThread] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [username, setUsername] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [newImage, setNewImage] = useState("");
  const bottomOfChatRef = useRef();
  const [reloadTickets, setReloadTickets] = useState(0);

  const fetchTickets = isAdmin ? getAllUsersTickets : getTickets;

  useEffect(() => {
    fetchTickets().then((response) => {
      setTickets(response.data.message);
      if (response.data.message?.length)
        setCurrentTicketId(response.data.message[0]);
    });
  }, []);

  useEffect(() => {
    if (reloadTickets !== 0) {
      fetchTickets().then((response) => {
        setTickets(response.data.message);
        setCurrentTicketId((prev) => prev);
      });
    }
  }, [reloadTickets]);

  useEffect(() => {
    setThread(null);
    if (currentTicketId) {
      getTicketThread(currentTicketId.ticket_id).then((response) => {
        if (response.data.message !== "error")
          setThread(response.data.message.reverse());
      });
    }
  }, [currentTicketId]);

  useEffect(() => {
    getUserFirstLast().then((response) => {
      setUsername(response.data.first_name + " " + response.data.last_name);
      setUserEmail(response.data.email);
    });
  }, []);

  useEffect(() => {
    bottomOfChatRef.current?.scrollIntoView({ block: "end" });
  }, [thread]);

  function clickScreenshot(source) {
    setIsScreenshotPopUpOpen(true);
    setCurrentScreenshotForPopUp(source);
  }

  const handlePaste = async (e) => {
    const items = e.clipboardData.items;
    for (const item of items) {
      if (item.type.startsWith("image/")) {
        const file = item.getAsFile();
        if (file) {
          const base64 = await fileToBase64(file);
          setNewImage(base64);
        }
      }
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    const imageFile = files.find((file) => file.type.startsWith("image/"));
    if (imageFile) {
      const base64 = await fileToBase64(imageFile);
      setNewImage(base64);
    }
  };

  const handleDragOver = (e) => e.preventDefault();

  function sendMessage() {
    if (!currentTicketId) return;
    const timestampSec = Math.floor(Date.now() / 1000);
    addNewTicketData(
      currentTicketId.ticket_id,
      {
        media: newImage,
        timestamp: timestampSec,
        description: newMessage,
        userEmail,
        username,
      },
      newImage,
    ).then((response) => {
      if (response.data.message === "ok") {
        setThread((prev = []) => [
          ...prev,
          {
            media: newImage,
            timestamp: timestampSec,
            ticket_id: currentTicketId.ticket_id,
            ticket_data: { description: newMessage, userEmail, username },
          },
        ]);
      }
    });
    setNewImage("");
    setNewMessage("");
  }

  function updateStatus(newStatus) {
    if (!currentTicketId) return;
    addNewTicketData(currentTicketId.ticket_id, null, null, newStatus).then(
      (response) => {
        if (response.data.message === "ok") setReloadTickets((r) => r + 1);
      },
    );
  }

  return (
    <div className="flex flex-row w-full h-full gap-0">
      {/* ── Ticket list ── */}
      <div className="flex flex-col w-72 shrink-0 h-full border-r border-mainDivider">
        <div className="flex-1 min-h-0 overflow-y-auto space-y-2 px-3 py-3">
          {tickets?.map((tick) => {
            const active = currentTicketId?.ticket_id === tick.ticket_id;
            const isOpen =
              tick.ticket_status === "open" || tick.ticket_status == null;
            return (
              <button
                key={tick.ticket_id}
                onClick={() => setCurrentTicketId(tick)}
                className={[
                  "relative w-full text-left px-4 py-3 rounded-lg",
                  "border transition-all duration-200",
                  active
                    ? "border-surfaceBorder bg-surface/80"
                    : "border-inputBorder bg-transparent text-mutedText hover:bg-selectedSection hover:border-surfaceBorder",
                ].join(" ")}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-mainText break-words">
                      {tick.ticket_data?.title || "-"}
                    </p>
                    {isAdmin && tick.ticket_data?.userEmail && (
                      <p className="text-xs text-mutedText mt-1">
                        {getLocalPart(tick.ticket_data.userEmail)}
                      </p>
                    )}
                  </div>
                  <span
                    className={[
                      "inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-wide shrink-0",
                      isOpen ? "text-activeCase" : "text-errorText",
                    ].join(" ")}
                  >
                    {isOpen ? "open" : "closed"}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Thread panel ── */}
      <div className="relative flex-1 min-w-0 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 px-5 py-4 border-b border-mainDivider">
          <h1
            className="text-base font-medium text-mainText flex-1 min-w-0"
            title={currentTicketId?.ticket_data?.title}
          >
            {currentTicketId?.ticket_data?.title || "-"}
          </h1>
          {currentTicketId && (
            <Button
              variant={
                currentTicketId.ticket_status === "open" ||
                !currentTicketId.ticket_status
                  ? "cautionary"
                  : "optimistic"
              }
              onClick={() =>
                updateStatus(
                  currentTicketId.ticket_status === "open" ||
                    !currentTicketId.ticket_status
                    ? "closed"
                    : "open",
                )
              }
            >
              {currentTicketId.ticket_status === "open" ||
              !currentTicketId.ticket_status
                ? "Close Ticket"
                : "Reopen Ticket"}
            </Button>
          )}
        </div>

        {/* Messages */}
        <div className="flex-1 min-h-0 overflow-auto px-5 py-4">
          {thread?.map((message, index) => (
            <div
              key={message.id || `${message.timestamp}-${index}`}
              className="mb-4"
            >
              <div className="px-4 py-3 rounded-lg border border-inputBorder bg-innerBg">
                <div className="flex items-center justify-between gap-3 mb-2">
                  {message?.ticket_data?.userEmail ? (
                    <span className="text-xs text-mutedText">
                      {getLocalPart(message.ticket_data.userEmail)}
                    </span>
                  ) : isAdmin ? (
                    <span className="text-xs text-mutedText">system</span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-xs text-mutedText">
                      <MdSupportAgent
                        className="h-3.5 w-3.5 text-velBlue"
                        aria-hidden="true"
                      />
                      support
                    </span>
                  )}
                  <span className="text-[11px] text-mutedText">
                    {formatTimestamp(message.timestamp)}
                  </span>
                </div>
                <div className="space-y-3">
                  {message?.ticket_data?.description && (
                    <p className="text-sm text-mainText whitespace-pre-wrap leading-relaxed">
                      {message.ticket_data.description}
                    </p>
                  )}
                  {message?.ticket_data?.steps &&
                    message.ticket_data.steps !== "" && (
                      <p className="text-sm text-mainText whitespace-pre-wrap leading-relaxed">
                        {message.ticket_data.steps}
                      </p>
                    )}
                  {message?.media && message.media !== "" && (
                    <img
                      src={message.media}
                      onClick={() => clickScreenshot(message.media)}
                      className="max-w-md w-full h-auto rounded-lg border border-surfaceBorder cursor-pointer"
                      alt=""
                    />
                  )}
                  {isAdmin && message?.ticket_data?.clientData && (
                    <div className="rounded-lg border border-inputBorder bg-innerBg p-3 overflow-auto">
                      {message.ticket_data.clientData.pageContext && (
                        <pre className="text-xs text-mutedText whitespace-pre-wrap break-words">
                          {JSON.stringify(
                            message.ticket_data.clientData.pageContext,
                            null,
                            2,
                          )}
                        </pre>
                      )}
                      {message.ticket_data.clientData.browserInfo && (
                        <pre className="mt-3 text-xs text-mutedText whitespace-pre-wrap break-words">
                          {JSON.stringify(
                            message.ticket_data.clientData.browserInfo,
                            null,
                            2,
                          )}
                        </pre>
                      )}
                      {message.ticket_data.clientData.screenInfo && (
                        <pre className="mt-3 text-xs text-mutedText whitespace-pre-wrap break-words">
                          {JSON.stringify(
                            message.ticket_data.clientData.screenInfo,
                            null,
                            2,
                          )}
                        </pre>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
          <div ref={bottomOfChatRef} />
        </div>

        {/* Reply box */}
        <div className="px-5 py-4 border-t border-mainDivider bg-innerBg rounded-b-lg">
          <textarea
            rows={3}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onPaste={handlePaste}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            className="w-full resize-none rounded-lg border border-inputBorder bg-transparent text-mainText placeholder:text-mutedText text-sm px-3 py-2 outline-none ring-inputFocus/60 transition focus:ring-2 mb-3"
            placeholder="Type your message… (paste or drag image to attach)"
          />
          {newImage && (
            <div className="mb-3 inline-flex items-center gap-3 rounded-lg border border-surfaceBorder bg-innerBg px-3 py-2">
              <img
                src={newImage}
                alt="preview"
                className="w-14 h-14 object-cover rounded-lg"
              />
              <button
                onClick={() => setNewImage("")}
                className="text-xs text-mutedText hover:text-errorText transition cursor-pointer"
              >
                Remove
              </button>
            </div>
          )}
          <Button onClick={sendMessage} fullWidth>
            Send
          </Button>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   AdminPublicModels
───────────────────────────────────────────────────────────── */
const CATEGORY_ORDER = [
  "Major Providers",
  "Cloud Platforms",
  "Inference Providers",
  "Self-Hosted",
  "Other",
];

const formatCost = (costPerToken) => {
  if (!costPerToken && costPerToken !== 0) return "—";
  const perMillion = costPerToken * 1_000_000;
  if (perMillion < 0.01) return "<$0.01/M";
  return `$${perMillion.toFixed(2)}/M`;
};

const formatTokens = (count) => {
  if (!count) return "—";
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(0)}M`;
  if (count >= 1_000) return `${(count / 1_000).toFixed(0)}K`;
  return `${count}`;
};

const CapabilityBadge = ({ label, active }) =>
  active ? (
    <span className="inline-flex items-center rounded-full border border-velBlue/20 bg-velBlue/10 px-1.5 py-0.5 text-[10px] text-velBlue">
      {label}
    </span>
  ) : null;

const PROVIDER_ICONS = {
  openai: OpenAIIcon,
  anthropic: AnthropicIcon,
  gemini: GeminiIcon,
  xai: XAIIcon,
  mistral: MistralIcon,
  deepseek: DeepSeekIcon,
  cohere: CohereIcon,
  azure: AzureIcon,
  azure_ai: AzureAIIcon,
  bedrock: BedrockIcon,
  sagemaker: AwsIcon,
  vertex_ai: VertexAIIcon,
  vertex_ai_beta: VertexAIIcon,
  groq: GroqIcon,
  fireworks_ai: FireworksIcon,
  together_ai: TogetherAIIcon,
  deepinfra: DeepInfraIcon,
  replicate: ReplicateIcon,
  perplexity: PerplexityIcon,
  cerebras: CerebrasIcon,
  ai21: Ai21Icon,
  cloudflare: CloudflareIcon,
  databricks: null,
  ollama: OllamaIcon,
  ollama_chat: OllamaIcon,
  huggingface: HuggingFaceIcon,
  openrouter: OpenRouterIcon,
  vllm: VllmIcon,
  anyscale: AnyscaleIcon,
  lm_studio: LMStudioIcon,
  nlp_cloud: NPLCloudIcon,
  "text-completion-openai": OpenAIIcon,
};

const ProviderIcon = ({ provider, displayName }) => {
  const Icon = PROVIDER_ICONS[provider];
  if (Icon) return Icon.Color ? <Icon.Color size={18} /> : <Icon size={18} />;
  return (
    <span
      className="inline-flex items-center justify-center rounded-lg bg-innerBg border border-surfaceBorder text-[10px] font-medium text-mutedText shrink-0"
      style={{ width: "1.125rem", height: "1.125rem" }}
    >
      {(displayName || provider || "?").charAt(0).toUpperCase()}
    </span>
  );
};

const AdminPublicModels = () => {
  const [catalog, setCatalog] = useState([]);
  const [savedConfigs, setSavedConfigs] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedProvider, setExpandedProvider] = useState(null);
  const [pendingCreds, setPendingCreds] = useState({});
  const [pendingModels, setPendingModels] = useState({});
  const [pendingEnabled, setPendingEnabled] = useState({});
  const [saving, setSaving] = useState({});
  const [unlockedFields, setUnlockedFields] = useState({});
  const [fieldSaving, setFieldSaving] = useState({});
  const [toast, setToast] = useState({ show: false, type: "success" });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [catalogRes, savedRes] = await Promise.all([
          getLitellmModels(),
          getPublicModels(),
        ]);
        const catalogData = catalogRes?.data?.data || [];
        setCatalog(catalogData);
        const saved = {};
        for (const s of savedRes?.data?.data || []) saved[s.provider] = s;
        setSavedConfigs(saved);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const showToast = (type) => {
    setToast({ show: true, type });
    setTimeout(() => setToast({ show: false, type }), 4000);
  };

  const getCredValue = (provider, key) =>
    pendingCreds[provider]?.[key] !== undefined
      ? pendingCreds[provider][key]
      : "";
  const getCredPlaceholder = (provider, key) => {
    const saved = savedConfigs[provider];
    if (saved?.credentials_masked?.[key]) return saved.credentials_masked[key];
    const catalogEntry = catalog.find((c) => c.provider === provider);
    const field = catalogEntry?.auth_fields?.find((f) => f.key === key);
    return field?.placeholder || "...";
  };
  const updateCredField = (provider, key, value) => {
    setPendingCreds((prev) => ({
      ...prev,
      [provider]: { ...(prev[provider] || {}), [key]: value },
    }));
  };
  const getEnabledModels = (provider) =>
    pendingModels[provider] !== undefined
      ? pendingModels[provider]
      : savedConfigs[provider]?.enabled_models || [];
  const toggleModel = (provider, modelKey) => {
    const current = getEnabledModels(provider);
    const next = current.includes(modelKey)
      ? current.filter((k) => k !== modelKey)
      : [...current, modelKey];
    setPendingModels((prev) => ({ ...prev, [provider]: next }));
  };
  const isProviderEnabled = (provider) =>
    pendingEnabled[provider] !== undefined
      ? pendingEnabled[provider]
      : savedConfigs[provider]?.enabled || false;
  const toggleProviderEnabled = (provider) => {
    setPendingEnabled((prev) => ({
      ...prev,
      [provider]: !isProviderEnabled(provider),
    }));
  };
  const hasChanges = (provider) =>
    pendingCreds[provider] ||
    pendingModels[provider] !== undefined ||
    pendingEnabled[provider] !== undefined;

  const saveProvider = async (provider) => {
    setSaving((prev) => ({ ...prev, [provider]: true }));
    const credentials = pendingCreds[provider] || {};
    const enabled = isProviderEnabled(provider);
    const enabled_models = getEnabledModels(provider);
    let success = false;
    try {
      const response = await updatePublicModel(
        provider,
        credentials,
        enabled,
        enabled_models,
      );
      if (response?.data?.message === "success") {
        const savedRes = await getPublicModels();
        const saved = {};
        for (const s of savedRes?.data?.data || []) saved[s.provider] = s;
        setSavedConfigs(saved);
        setPendingCreds((prev) => {
          const n = { ...prev };
          delete n[provider];
          return n;
        });
        setPendingModels((prev) => {
          const n = { ...prev };
          delete n[provider];
          return n;
        });
        setPendingEnabled((prev) => {
          const n = { ...prev };
          delete n[provider];
          return n;
        });
        showToast("success");
        success = true;
      } else showToast("error");
    } catch {
      showToast("error");
    } finally {
      setSaving((prev) => ({ ...prev, [provider]: false }));
    }
    return success;
  };

  const unlockField = (provider, key) => {
    setUnlockedFields((prev) => ({ ...prev, [`${provider}:${key}`]: true }));
  };

  const saveField = async (provider, key) => {
    const fieldId = `${provider}:${key}`;
    const value = pendingCreds[provider]?.[key] ?? "";
    if (!value.trim()) {
      setUnlockedFields((prev) => {
        const n = { ...prev };
        delete n[fieldId];
        return n;
      });
      setPendingCreds((prev) => {
        const n = { ...prev };
        if (n[provider]) delete n[provider][key];
        return n;
      });
      return;
    }
    setFieldSaving((prev) => ({ ...prev, [fieldId]: true }));
    const success = await saveProvider(provider);
    setFieldSaving((prev) => {
      const n = { ...prev };
      delete n[fieldId];
      return n;
    });
    if (success) {
      setUnlockedFields((prev) => {
        const n = { ...prev };
        delete n[fieldId];
        return n;
      });
    }
  };

  const filteredCatalog = useMemo(() => {
    if (!searchTerm.trim()) return catalog;
    const q = searchTerm.toLowerCase();
    return catalog
      .map((provider) => {
        const nameMatch =
          provider.display_name.toLowerCase().includes(q) ||
          provider.provider.toLowerCase().includes(q);
        const matchingModels = provider.models.filter((m) =>
          m.model_key.toLowerCase().includes(q),
        );
        if (nameMatch) return provider;
        if (matchingModels.length > 0)
          return { ...provider, models: matchingModels };
        return null;
      })
      .filter(Boolean);
  }, [catalog, searchTerm]);

  const grouped = useMemo(() => {
    const groups = {};
    for (const provider of filteredCatalog) {
      const cat = provider.category || "Other";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(provider);
    }
    for (const cat of Object.keys(groups)) {
      groups[cat].sort((a, b) =>
        a.display_name
          .toLowerCase()
          .localeCompare(b.display_name.toLowerCase()),
      );
    }
    return groups;
  }, [filteredCatalog]);

  const sortedCategories = CATEGORY_ORDER.filter((c) => grouped[c]);

  if (loading) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <div className="text-sm text-mutedText animate-pulse">
          Loading model catalog...
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full flex flex-col">
      {/* Header + Search */}
      <div className="shrink-0 px-6 py-4 space-y-3 border-b border-mainDivider">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-medium text-mainText">Public Models</h2>
          <span className="text-xs text-mutedText">
            {catalog.length} providers ·{" "}
            {catalog.reduce((s, p) => s + p.models.length, 0)} models
          </span>
        </div>
        {/* VN search bar pattern */}
        <div className="relative">
          <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-mutedText">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </div>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search providers or models..."
            autoComplete="off"
            className="w-full rounded-lg border border-inputBorder bg-transparent pl-10 pr-3 py-2.5 text-sm text-mainText outline-none ring-inputFocus/60 transition focus:ring-2 placeholder:text-mutedText"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-mutedText hover:text-mainText transition cursor-pointer"
              aria-label="Clear search"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Scrollable provider list */}
      <div className="flex-1 min-h-0 overflow-auto overscroll-contain px-6 pb-6">
        {sortedCategories.length === 0 && (
          <div className="text-sm text-mutedText text-center py-8">
            No providers or models match "{searchTerm}"
          </div>
        )}

        {sortedCategories.map((category) => (
          <div key={category} className="mb-5">
            {/* Category header */}
            <div className="py-1.5 mb-2">
              <h3 className="text-xs font-medium text-mutedText uppercase tracking-wider">
                {category}
              </h3>
            </div>

            <div className="space-y-2">
              {grouped[category].map((providerData) => {
                const pid = providerData.provider;
                const isExpanded = expandedProvider === pid;
                const saved = savedConfigs[pid];
                const hasCreds = saved?.has_credentials || false;
                const enabled = isProviderEnabled(pid);
                const enabledModels = getEnabledModels(pid);
                const dirty = hasChanges(pid);

                return (
                  <div
                    key={pid}
                    className={classNames(
                      "relative rounded-lg border transition-all duration-200",
                      isExpanded
                        ? "border-surfaceBorder bg-surface/80 shadow-login-panel backdrop-blur-md"
                        : "border-inputBorder bg-innerBg hover:bg-tableRowHover hover:border-surfaceBorder",
                    )}
                  >
                    <StripeOverlay rounded="rounded-lg" />

                    {/* Provider header */}
                    <button
                      onClick={() =>
                        setExpandedProvider(isExpanded ? null : pid)
                      }
                      className="relative z-10 w-full text-left p-3 flex items-center gap-3 cursor-pointer"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className={classNames(
                          "h-3.5 w-3.5 text-mutedText transition-transform duration-200 shrink-0",
                          isExpanded ? "rotate-90" : "",
                        )}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <polyline points="9 18 15 12 9 6" />
                      </svg>

                      <div className="flex-1 min-w-0 flex items-center gap-2.5">
                        <ProviderIcon
                          provider={pid}
                          displayName={providerData.display_name}
                        />
                        <span className="text-sm font-medium text-mainText truncate">
                          {providerData.display_name}
                        </span>
                        <span className="text-xs text-mutedText">
                          {providerData.models.length} model
                          {providerData.models.length !== 1 ? "s" : ""}
                        </span>
                        {hasCreds && (
                          <span className="inline-flex items-center rounded-full border border-activeCase/25 bg-activeCase/10 px-1.5 py-0.5 text-[10px] text-activeCase">
                            Configured
                          </span>
                        )}
                      </div>

                      {/* Enable toggle */}
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleProviderEnabled(pid);
                        }}
                      >
                        <Switch
                          checked={enabled}
                          onChange={() => {}}
                          className={classNames(
                            enabled
                              ? "bg-velBlue border-velBlue"
                              : "bg-transparent border-inputBorder",
                            "relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border transition-colors duration-200 outline-none",
                          )}
                        >
                          <span
                            className={classNames(
                              enabled
                                ? "translate-x-[18px]"
                                : "translate-x-0.5",
                              "pointer-events-none inline-block h-4 w-4 transform rounded-full bg-mainText shadow ring-0 transition duration-200",
                            )}
                          />
                        </Switch>
                      </div>
                    </button>

                    {/* Expanded panel */}
                    {isExpanded && (
                      <div className="relative z-10 border-t border-mainDivider p-4 space-y-4">
                        {/* Credentials */}
                        <div className="space-y-3">
                          <h4 className="text-xs font-medium text-mutedText uppercase tracking-wider">
                            Credentials
                          </h4>
                          {providerData.auth_fields.map((field) => {
                            const fieldId = `${pid}:${field.key}`;
                            const isUnlocked = !!unlockedFields[fieldId];
                            const isSaving = !!fieldSaving[fieldId];
                            const maskedValue =
                              savedConfigs[pid]?.credentials_masked?.[
                                field.key
                              ];

                            return (
                              <div key={field.key}>
                                <label className="mb-1.5 block text-[0.8125rem] font-normal tracking-wide text-mutedText">
                                  {field.label}
                                </label>

                                {!isUnlocked ? (
                                  <div className="flex items-stretch gap-2">
                                    <div className="flex-1 flex items-center gap-2 rounded-lg border border-inputBorder bg-innerBg px-3 py-2.5">
                                      <Lock className="h-3.5 w-3.5 text-mutedText shrink-0" />
                                      <span className="flex-1 text-sm font-mono text-mutedText truncate">
                                        {maskedValue || "Not configured"}
                                      </span>
                                    </div>
                                    <button
                                      onClick={() =>
                                        unlockField(pid, field.key)
                                      }
                                      className="shrink-0 rounded-lg border border-btnBorder bg-transparent px-2.5 text-mutedText transition hover:text-mainText hover:bg-btnHover outline-none ring-inputFocus/60 focus-visible:ring-2 cursor-pointer"
                                      aria-label="Edit credential"
                                    >
                                      <Pencil className="h-3.5 w-3.5" />
                                    </button>
                                  </div>
                                ) : field.multiline ? (
                                  <div className="space-y-2">
                                    <textarea
                                      rows={3}
                                      value={getCredValue(pid, field.key)}
                                      onChange={(e) =>
                                        updateCredField(
                                          pid,
                                          field.key,
                                          e.target.value,
                                        )
                                      }
                                      placeholder={getCredPlaceholder(
                                        pid,
                                        field.key,
                                      )}
                                      disabled={isSaving}
                                      className="w-full rounded-lg border border-velBlue/40 bg-transparent px-3 py-2.5 text-sm text-mainText outline-none ring-inputFocus/60 transition focus:ring-2 placeholder:text-mutedText font-mono resize-none disabled:opacity-50"
                                    />
                                    <div className="flex justify-end">
                                      <button
                                        onClick={() =>
                                          saveField(pid, field.key)
                                        }
                                        disabled={isSaving}
                                        className="inline-flex items-center gap-1.5 rounded-lg border border-velBlue/40 bg-velBlue/10 px-3 py-1.5 text-xs text-velBlue transition hover:bg-velBlue/20 outline-none ring-inputFocus/60 focus-visible:ring-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                      >
                                        {isSaving ? (
                                          <>
                                            <svg
                                              className="h-3.5 w-3.5 animate-spin"
                                              viewBox="0 0 24 24"
                                              fill="none"
                                              aria-hidden="true"
                                            >
                                              <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="3"
                                              />
                                              <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                              />
                                            </svg>
                                            Saving...
                                          </>
                                        ) : (
                                          <>
                                            <Lock className="h-3.5 w-3.5" />
                                            Save & lock
                                          </>
                                        )}
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex items-stretch gap-2">
                                    <input
                                      type="text"
                                      value={getCredValue(pid, field.key)}
                                      onChange={(e) =>
                                        updateCredField(
                                          pid,
                                          field.key,
                                          e.target.value,
                                        )
                                      }
                                      placeholder={getCredPlaceholder(
                                        pid,
                                        field.key,
                                      )}
                                      disabled={isSaving}
                                      className="flex-1 rounded-lg border border-velBlue/40 bg-transparent px-3 py-2.5 text-sm text-mainText outline-none ring-inputFocus/60 transition focus:ring-2 placeholder:text-mutedText font-mono disabled:opacity-50"
                                    />
                                    <button
                                      onClick={() => saveField(pid, field.key)}
                                      disabled={isSaving}
                                      className="shrink-0 rounded-lg border border-btnBorder bg-transparent px-2.5 text-mutedText transition hover:text-mainText hover:bg-btnHover outline-none ring-inputFocus/60 focus-visible:ring-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                      aria-label="Save and lock credential"
                                    >
                                      {isSaving ? (
                                        <svg
                                          className="h-3.5 w-3.5 animate-spin text-velBlue"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          aria-hidden="true"
                                        >
                                          <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="3"
                                          />
                                          <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                          />
                                        </svg>
                                      ) : (
                                        <Lock className="h-3.5 w-3.5" />
                                      )}
                                    </button>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        {/* Model list */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <h4 className="text-xs font-medium text-mutedText uppercase tracking-wider">
                              Models
                            </h4>
                            {enabledModels.length > 0 && (
                              <span className="text-[10px] text-mutedText">
                                {enabledModels.length} enabled
                              </span>
                            )}
                          </div>

                          {/* Column headers */}
                          <div className="grid grid-cols-[auto_1fr_72px_72px_72px_72px] gap-2 items-center px-2 py-1 text-[10px] text-mutedText uppercase tracking-wider">
                            <div className="w-4" />
                            <div>Model</div>
                            <div className="text-right">Input</div>
                            <div className="text-right">Output</div>
                            <div className="text-right">Ctx In</div>
                            <div className="text-right">Ctx Out</div>
                          </div>

                          <div className="max-h-64 overflow-y-auto space-y-0.5 rounded-lg border border-inputBorder bg-innerBg">
                            {providerData.models.map((model) => {
                              const isModelEnabled = enabledModels.includes(
                                model.model_key,
                              );
                              return (
                                <div
                                  key={model.model_key}
                                  className={classNames(
                                    "grid grid-cols-[auto_1fr_72px_72px_72px_72px] gap-2 items-center px-2 py-1.5 transition",
                                    isModelEnabled
                                      ? "bg-selectedSection"
                                      : "hover:bg-tableRowHover",
                                  )}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isModelEnabled}
                                    onChange={() =>
                                      toggleModel(pid, model.model_key)
                                    }
                                    className="h-3.5 w-3.5 rounded border-inputBorder bg-transparent text-velBlue outline-none ring-inputFocus/60 focus:ring-2 focus:ring-offset-0 cursor-pointer"
                                  />
                                  <div className="min-w-0">
                                    <div className="flex items-center gap-1.5">
                                      <span className="text-xs text-mainText truncate font-mono">
                                        {model.model_key}
                                      </span>
                                      <div className="flex gap-1 shrink-0 flex-wrap">
                                        <CapabilityBadge
                                          label="Vision"
                                          active={model.supports_vision}
                                        />
                                        <CapabilityBadge
                                          label="Tools"
                                          active={
                                            model.supports_function_calling
                                          }
                                        />
                                        <CapabilityBadge
                                          label="JSON"
                                          active={
                                            model.supports_response_schema
                                          }
                                        />
                                        <CapabilityBadge
                                          label="Reasoning"
                                          active={model.supports_reasoning}
                                        />
                                        <CapabilityBadge
                                          label="Caching"
                                          active={model.supports_prompt_caching}
                                        />
                                        <CapabilityBadge
                                          label="Search"
                                          active={model.supports_web_search}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                  <div className="text-[11px] text-mutedText text-right font-mono">
                                    {formatCost(model.input_cost_per_token)}
                                  </div>
                                  <div className="text-[11px] text-mutedText text-right font-mono">
                                    {formatCost(model.output_cost_per_token)}
                                  </div>
                                  <div className="text-[11px] text-mutedText text-right font-mono">
                                    {formatTokens(model.max_input_tokens)}
                                  </div>
                                  <div className="text-[11px] text-mutedText text-right font-mono">
                                    {formatTokens(model.max_output_tokens)}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* Save button */}
                        <div className="flex justify-end">
                          <button
                            onClick={() => saveProvider(pid)}
                            disabled={!dirty && !saving[pid]}
                            className={classNames(
                              "rounded-lg px-4 py-1.5 text-sm font-normal transition border outline-none ring-inputFocus/60 focus-visible:ring-2",
                              dirty
                                ? "border-velBlue/40 bg-velBlue/10 text-velBlue hover:bg-velBlue/20 cursor-pointer"
                                : "border-surfaceBorder bg-transparent text-mutedText cursor-not-allowed",
                            )}
                          >
                            {saving[pid]
                              ? "Saving..."
                              : dirty
                                ? "Save Changes"
                                : "Saved"}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      {/* Toast */}
      <div className="fixed bottom-4 right-4 z-50">
        <Toast
          show={toast.show}
          type={toast.type}
          onClose={() => setToast({ show: false, type: toast.type })}
        />
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   MainSettings
───────────────────────────────────────────────────────────── */
const MainSettings = ({ onClose }) => {
  const [settingPage, setSettingPage] = useState("user:general");
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    getUserType().then((response) => {
      if (response.data?.role === "admin") setIsAdmin(true);
    });
  }, []);

  // Nav item — flat row, active state uses tint + velBlue text + left accent line
  const NavItem = ({ label, active, onClick }) => (
    <button
      onClick={onClick}
      className={[
        "w-full text-left px-3 py-2 rounded-lg transition-all duration-200",
        "text-sm font-normal",
        active
          ? "bg-selectedSection text-velBlue"
          : "text-mutedText hover:bg-mainButtonBg/50 hover:text-mainText",
      ].join(" ")}
    >
      <div className="flex items-center gap-2.5">
        <span
          className={`inline-block h-2 w-2 rounded-full shrink-0 ${active ? "bg-velBlue" : "bg-mutedText/30"}`}
          style={{
            boxShadow: active ? "0 0 6px rgba(14,165,233,0.4)" : "none",
          }}
          aria-hidden="true"
        />
        {label}
      </div>
    </button>
  );

  return (
    <div className="flex h-full w-full overflow-hidden font-poppins font-normal text-mainText">
      {/* ── Left nav sidebar ── */}
      <div className="w-56 shrink-0 flex flex-col border-r border-mainDivider">
        {/* Sidebar header */}
        <div className="px-4 py-4 border-b border-mainDivider">
          <p className="text-xs font-medium text-mutedText uppercase tracking-widest">
            Settings
          </p>
        </div>

        {/* Nav items */}
        <div className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
          {isAdmin && (
            <>
              <p className="px-3 pb-1 pt-1 text-[10px] font-normal tracking-widest uppercase text-mutedText/60">
                Admin
              </p>
              <NavItem
                label="User Management"
                active={settingPage === "admin:usermanagement"}
                onClick={() => setSettingPage("admin:usermanagement")}
              />
              <NavItem
                label="Security"
                active={settingPage === "admin:security"}
                onClick={() => setSettingPage("admin:security")}
              />
              <NavItem
                label="Tickets"
                active={settingPage === "admin:tickets"}
                onClick={() => setSettingPage("admin:tickets")}
              />
              <NavItem
                label="Public Models"
                active={settingPage === "admin:publicmodels"}
                onClick={() => setSettingPage("admin:publicmodels")}
              />
              <div className="mx-2 my-3 h-px bg-divider-glow" />
              <p className="px-3 pb-1 text-[10px] font-normal tracking-widest uppercase text-mutedText/60">
                User
              </p>
            </>
          )}
          <NavItem
            label="General"
            active={settingPage === "user:general"}
            onClick={() => setSettingPage("user:general")}
          />
          <NavItem
            label="Security"
            active={settingPage === "user:security"}
            onClick={() => setSettingPage("user:security")}
          />
          <NavItem
            label="Tickets"
            active={settingPage === "user:tickets"}
            onClick={() => setSettingPage("user:tickets")}
          />
        </div>
      </div>

      {/* ── Right content area ── */}
      <div className="flex-1 min-w-0 min-h-0 overflow-auto">
        {(() => {
          if (settingPage === "admin:usermanagement")
            return <AdminUserManagement />;
          if (settingPage === "admin:security") return <AdminSecurity />;
          if (settingPage === "admin:tickets") return <Tickets isAdmin />;
          if (settingPage === "admin:publicmodels")
            return <AdminPublicModels />;
          if (settingPage === "user:general") return <UserGeneral />;
          if (settingPage === "user:security") return <UserSecurity />;
          if (settingPage === "user:tickets") return <Tickets />;
          return null;
        })()}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   PopUpSettings (default export)
───────────────────────────────────────────────────────────── */
const PopUpSettings = () => {
  const { setIsPopUpOpen } = usePopup();
  const close = () => setIsPopUpOpen(false);
  const cardRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Close when clicking the backdrop (outside the card)
  const handleBackdropClick = (e) => {
    if (cardRef.current && !cardRef.current.contains(e.target)) {
      close();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-mainBg/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Settings"
      onClick={handleBackdropClick}
    >
      {/* Unified card — click inside does NOT close */}
      <div
        ref={cardRef}
        className="relative flex flex-col w-[min(96vw,1200px)] h-[min(92vh,800px)] rounded-lg border border-surfaceBorder bg-surface/80 shadow-login-panel backdrop-blur-md overflow-hidden"
      >
        {/* Top bar — always on top, never scrolled over */}
        <div className="flex items-center justify-end px-3 py-2 border-b border-mainDivider shrink-0">
          <button
            onClick={close}
            aria-label="Close settings"
            className="p-1.5 rounded-lg transition text-mutedText hover:bg-btnHover hover:text-mainText outline-none ring-inputFocus/60 focus-visible:ring-2 cursor-pointer"
          >
            <X strokeWidth={1.5} className="h-4 w-4" />
          </button>
        </div>
        {/* Sidebar + content fill remaining height */}
        <div className="flex flex-1 min-h-0">
          <MainSettings onClose={close} />
        </div>
      </div>
    </div>
  );
};

export default PopUpSettings;
